import type { PedidoVentaDetalle } from '../services/pedidoVentaService';
import { resolverPrecioListaDetalle } from './normalizarDetallesFacturaVenta';

export type DetalleLineaVentaPrecio = Pick<
  PedidoVentaDetalle,
  'precioUnitario' | 'precioLista' | 'descuento' | 'porcDesc' | 'codigoArticulo' | 'descripcionArticulo' | 'cantidad'
>;

/** Precio neto unitario (lista menos descuento %) de una línea de venta. */
export const precioNetoLineaPedido = (detalle: DetalleLineaVentaPrecio): number => {
  const precioLista = resolverPrecioListaDetalle(detalle);
  const desc = Number(detalle.porcDesc ?? detalle.descuento ?? 0);
  return precioLista * (1 - desc / 100);
};

export const lineaTienePrecioCero = (detalle: DetalleLineaVentaPrecio): boolean =>
  precioNetoLineaPedido(detalle) <= 0;

export const mensajeAdvertenciaPrecioCero = (
  detalle: Pick<DetalleLineaVentaPrecio, 'descripcionArticulo' | 'codigoArticulo'>,
  index: number,
  tipoDocumento: 'pedido' | 'factura' | 'remito' = 'pedido',
): string => {
  const etiqueta =
    tipoDocumento === 'factura' ? 'la factura' : tipoDocumento === 'remito' ? 'el remito' : 'el pedido';
  return `El producto "${detalle.descripcionArticulo || detalle.codigoArticulo}" (ítem ${index + 1}) tiene precio cero. Si es intencional, puede guardar ${etiqueta} de todas formas.`;
};

/** @deprecated Usar mensajeAdvertenciaPrecioCero */
export const mensajeAdvertenciaPrecioCeroPedido = (
  detalle: Pick<DetalleLineaVentaPrecio, 'descripcionArticulo' | 'codigoArticulo'>,
  index: number,
): string => mensajeAdvertenciaPrecioCero(detalle, index, 'pedido');

/** Precio de lista en pantalla (el estado guarda lista en precioUnitario). */
export const precioListaDetallePedido = (
  detalle: Pick<PedidoVentaDetalle, 'precioUnitario' | 'precioLista'>,
): number => {
  const listaExplicita = detalle.precioLista;
  if (listaExplicita != null && Number.isFinite(Number(listaExplicita))) {
    return Number(listaExplicita);
  }
  const precioPantalla = detalle.precioUnitario;
  if (precioPantalla != null && Number.isFinite(Number(precioPantalla))) {
    return Number(precioPantalla);
  }
  return 0;
};

/**
 * El POST /api/PedidosVenta rechaza ítems con precio 0 en create.
 * Se crea con precio simbólico o sin las líneas en cero y luego se actualiza con PUT.
 */
export const prepararDetallesParaCreateConPrecioCero = <T extends DetalleLineaVentaPrecio>(
  detalles: T[],
): T[] => {
  if (detalles.length === 0) return detalles;

  const sinPrecioCero = detalles.filter((d) => !lineaTienePrecioCero(d));
  if (sinPrecioCero.length > 0) {
    return sinPrecioCero;
  }

  const primera = detalles[0];
  const cantidad = Number(primera.cantidad ?? 1);
  return [{
    ...primera,
    precioUnitario: 0.01,
    subtotal: Math.round(cantidad * 0.01 * 100) / 100,
  }];
};

export const pedidoTieneLineasConPrecioCero = (
  detalles: DetalleLineaVentaPrecio[] | undefined,
): boolean => Boolean(detalles?.some(lineaTienePrecioCero));

export const advertirLineasConPrecioCero = (
  detalles: DetalleLineaVentaPrecio[] | undefined,
  enqueueSnackbar: (message: string, options: { variant: 'warning'; autoHideDuration?: number }) => void,
  tipoDocumento: 'pedido' | 'factura' | 'remito' = 'pedido',
): void => {
  detalles?.forEach((detalle, index) => {
    if (lineaTienePrecioCero(detalle)) {
      enqueueSnackbar(mensajeAdvertenciaPrecioCero(detalle, index, tipoDocumento), {
        variant: 'warning',
        autoHideDuration: 6000,
      });
    }
  });
};

/** @deprecated Usar advertirLineasConPrecioCero */
export const advertirLineasConPrecioCeroPedido = (
  detalles: DetalleLineaVentaPrecio[] | undefined,
  enqueueSnackbar: (message: string, options: { variant: 'warning'; autoHideDuration?: number }) => void,
): void => advertirLineasConPrecioCero(detalles, enqueueSnackbar, 'pedido');

/** Precio de lista desde pendiente de seguimiento (respeta 0 explícito en precioListaMaster). */
export const precioListaDesdePendienteMaster = (pendiente: {
  precioListaMaster?: number | null;
  precioUnitarioMaster?: number | null;
}): number => {
  if (pendiente.precioListaMaster != null && Number.isFinite(Number(pendiente.precioListaMaster))) {
    return Number(pendiente.precioListaMaster);
  }
  if (pendiente.precioUnitarioMaster != null && Number.isFinite(Number(pendiente.precioUnitarioMaster))) {
    return Number(pendiente.precioUnitarioMaster);
  }
  return 0;
};

export const prepararItemsParaCreateConPrecioCero = <T extends DetalleLineaVentaPrecio>(
  items: T[],
): T[] => prepararDetallesParaCreateConPrecioCero(items);
